import React, { useState } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import WebsiteLinksInput from './components/WebsiteLinksInput';
import CategorizedSkillsInput from './components/CategorizedSkillsInput';

const SecurityResumeForm = ({ resumeData, onUpdate, validationErrors = {} }) => {
  const [activeSection, setActiveSection] = useState('contact');
  const [newSkill, setNewSkill] = useState('');
  const [newSoftSkill, setNewSoftSkill] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [newWebsite, setNewWebsite] = useState("");

  const updateField = (field, data) => {
    onUpdate(prev => ({ ...prev, [field]: data }));
  };

  const handlePersonalChange = (field, value) => {
    onUpdate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        handlePersonalChange('profile_image_url', result.file_url || URL.createObjectURL(file));
      } else {
        handlePersonalChange('profile_image_url', URL.createObjectURL(file));
      }
    } catch (error) {
      handlePersonalChange('profile_image_url', URL.createObjectURL(file));
    }
    setIsUploading(false);
  };

  const addSkill = (category) => {
    const skillValue = category === 'soft_skills' ? newSoftSkill : newSkill;
    if (skillValue.trim()) {
      const currentSkills = resumeData[category] || [];
      updateField(category, [...currentSkills, skillValue.trim()]);
      if (category === 'soft_skills') {
        setNewSoftSkill("");
      } else {
        setNewSkill("");
      }
    }
  };

  const removeSkill = (category, index) => {
    const currentSkills = resumeData[category] || [];
    updateField(category, currentSkills.filter((_, i) => i !== index));
  };

  const addExperience = () => {
    updateField("work_experience", [...(resumeData.work_experience || []), {
      position: "",
      company: "",
      location: "",
      start_date: "",
      end_date: "",
      responsibilities: [""]
    }]);
  };

  const updateExperience = (index, field, value) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("work_experience", updated);
  };

  const addResponsibility = (expIndex) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex].responsibilities.push("");
    updateField("work_experience", updated);
  };

  const updateResponsibility = (expIndex, pointIndex, value) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex].responsibilities[pointIndex] = value;
    updateField("work_experience", updated);
  };

  const removeResponsibility = (expIndex, pointIndex) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex].responsibilities.splice(pointIndex, 1);
    updateField("work_experience", updated);
  };

  const removeExperience = (index) => {
    updateField("work_experience", resumeData.work_experience.filter((_, i) => i !== index));
  };
  
  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      institution: "",
      start_date: "",
      end_date: "",
      description: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", resumeData.education.filter((_, i) => i !== index));
  };

  const addLanguage = () => {
    updateField("languages", [...(resumeData.languages || []), ""]);
  };

  const updateLanguage = (index, value) => {
    const updated = [...(resumeData.languages || [])];
    updated[index] = value;
    updateField("languages", updated);
  };

  const removeLanguage = (index) => {
    updateField("languages", resumeData.languages.filter((_, i) => i !== index));
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };

  const removeWebsite = (index) => {
    updateField("websites", resumeData.websites.filter((_, i) => i !== index));
  };

  const addProject = () => {
    const newProjectObj = { title: "", description: "", link: "", year: "", technologies: [] };
    updateField("projects", [...(resumeData.projects || []), newProjectObj]);
  };

  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };

  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    
    if (field === 'technologies') {
      updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'tech_stack') {
      updated[index].technologies = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'link') {
      updated[index].github_link = value;
    }
    if (field === 'github_link') {
      updated[index].link = value;
    }
    
    updateField("projects", updated);
  };

  const addAchievement = () => {
    updateField("achievements", [...(resumeData.achievements || []), ""]);
  };

  const updateAchievement = (index, value) => {
    const updated = [...(resumeData.achievements || [])];
    updated[index] = value;
    updateField("achievements", updated);
  };

  const removeAchievement = (index) => {
    updateField("achievements", resumeData.achievements.filter((_, i) => i !== index));
  };

  const addCertification = () => {
    updateField("certifications", [...(resumeData.certifications || []), { name: "", provider: "" }]);
  };

  const updateCertification = (index, field, value) => {
    const updated = [...(resumeData.certifications || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("certifications", updated);
  };

  const removeCertification = (index) => {
    updateField("certifications", resumeData.certifications.filter((_, i) => i !== index));
  };

  const sections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'languages', label: 'Languages' },
    { id: 'websites', label: 'Websites' },
    { id: 'projects', label: 'Projects' }
  ];

  return (
    <div className="resume-form" style={{marginTop: 0, paddingTop: 0}}>
      <div className="form-content" style={{marginTop: 0, paddingTop: 0}}>
        <div className="form-header">
          <h2 className="form-title">Security Resume Editor</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
          {activeSection === 'contact' && (
            <div className="tab-panel">
              <h3 className="section-title">Contact Information</h3>
              
              <div className={`form-group ${validationErrors.profile_image_url ? 'error' : ''}`}>
                <label>Profile Picture</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  {resumeData?.profile_image_url && (
                    <img 
                      src={resumeData.profile_image_url} 
                      alt="Profile Preview" 
                      style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ddd' }}
                    />
                  )}
                  <div>
                    <input
                      type="file"
                      id="profile-image-upload"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                      disabled={isUploading}
                    />
                    <label htmlFor="profile-image-upload" className="add-button" style={{ cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.7 : 1 }}>
                      {isUploading ? 'Uploading...' : 'Upload Photo'}
                    </label>
                    {resumeData?.profile_image_url && (
                       <button 
                         type="button"
                         onClick={() => handlePersonalChange('profile_image_url', '')} 
                         className="remove-button" 
                         style={{marginLeft: '10px'}}
                       >
                         🗑️ Remove
                       </button>
                    )}
                  </div>
                </div>
                {validationErrors.profile_image_url && <p className="error-message">{validationErrors.profile_image_url}</p>}
              </div>

              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.full_name ? 'error' : ''}`}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={resumeData?.full_name || ""}
                    onChange={(e) => handlePersonalChange("full_name", e.target.value)}
                    placeholder="John Doe"
                  />
                  {validationErrors.full_name && <p className="error-message">{validationErrors.full_name}</p>}
                </div>
                <div className={`form-group ${validationErrors.job_title ? 'error' : ''}`}>
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={resumeData?.job_title || ""}
                    onChange={(e) => handlePersonalChange("job_title", e.target.value)}
                    placeholder="Security Manager"
                  />
                  {validationErrors.job_title && <p className="error-message">{validationErrors.job_title}</p>}
                </div>
                <div className={`form-group ${validationErrors.email ? 'error' : ''}`}>
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={resumeData?.email || ""}
                    onChange={(e) => handlePersonalChange("email", e.target.value)}
                    placeholder="john.doe@email.com"
                  />
                  {validationErrors.email && <p className="error-message">{validationErrors.email}</p>}
                </div>
                <div className={`form-group ${validationErrors.phone ? 'error' : ''}`}>
                  <label>Phone Number</label>
                  <input
                    type="text"
                    value={resumeData?.phone || ""}
                    onChange={(e) => handlePersonalChange("phone", e.target.value)}
                    placeholder="+1-555-123-4567"
                  />
                  {validationErrors.phone && <p className="error-message">{validationErrors.phone}</p>}
                </div>
                <div className={`form-group ${validationErrors.location ? 'error' : ''}`}>
                  <label>Location</label>
                  <input
                    type="text"
                    value={resumeData?.location || ""}
                    onChange={(e) => handlePersonalChange("location", e.target.value)}
                    placeholder="San Francisco, CA"
                  />
                  {validationErrors.location && <p className="error-message">{validationErrors.location}</p>}
                </div>
                <div className={`form-group ${validationErrors.website ? 'error' : ''}`}>
                  <label>Website</label>
                  <input
                    type="text"
                    value={resumeData?.website || ""}
                    onChange={(e) => handlePersonalChange("website", e.target.value)}
                    placeholder="www.johndoe.com"
                  />
                  {validationErrors.website && <p className="error-message">{validationErrors.website}</p>}
                </div>
              </div>
              <WebsiteLinksInput
                websites={resumeData.websites || []}
                newWebsite={newWebsite}
                setNewWebsite={setNewWebsite}
                onAdd={addWebsite}
                onRemove={removeWebsite}
              />
            </div>
          )}

          {activeSection === 'summary' && (
            <div className="tab-panel">
               <h3 className="section-title">Professional Summary</h3>
              <div className={`form-group ${validationErrors.professional_summary ? 'error' : ''}`}>
                <textarea
                  value={resumeData?.professional_summary || ""}
                  onChange={(e) => updateField("professional_summary", e.target.value)}
                  placeholder="Experienced Security Manager with proven success in ensuring safety and security..."
                  rows={8}
                />
                {validationErrors.professional_summary && <p className="error-message">{validationErrors.professional_summary}</p>}
              </div>
            </div>
          )}

          {activeSection === 'experience' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Work Experience</h3>
                <button onClick={addExperience} className="add-button">
                  + Add Experience
                </button>
              </div>
              {(resumeData.work_experience || []).map((exp, index) => {
                const expErrors = validationErrors.work_experience?.[index] || {};
                return (
                  <div key={index} className="experience-card">
                    <div className="card-header">
                      <h4>Experience {index + 1}</h4>
                      <button onClick={() => removeExperience(index)} className="remove-button">
                        🗑️
                      </button>
                    </div>
                    <div className="card-content">
                      <div className="form-group-grid">
                        <div className={`form-group ${expErrors.position ? 'error' : ''}`}>
                          <label>Position</label>
                          <input type="text" value={exp.position || ""} onChange={(e) => updateExperience(index, "position", e.target.value)} placeholder="Security Manager" maxLength="70"/>
                          {expErrors.position && <p className="error-message">{expErrors.position}</p>}
                        </div>
                        <div className={`form-group ${expErrors.company ? 'error' : ''}`}>
                          <label>Company Name</label>
                          <input type="text" value={exp.company || ""} onChange={(e) => updateExperience(index, "company", e.target.value)} placeholder="Company Name | Location" maxLength="70"/>
                          {expErrors.company && <p className="error-message">{expErrors.company}</p>}
                        </div>
                      </div>
                      <div className="form-group-grid">
                        <div className={`form-group ${expErrors.start_date ? 'error' : ''}`}>
                          <label>Start Date</label>
                          <input type="text" value={exp.start_date || ""} onChange={(e) => updateExperience(index, "start_date", e.target.value)} placeholder="Jan 2020" maxLength="20"/>
                          {expErrors.start_date && <p className="error-message">{expErrors.start_date}</p>}
                        </div>
                        <div className={`form-group ${expErrors.end_date ? 'error' : ''}`}>
                          <label>End Date</label>
                          <input type="text" value={exp.end_date || ""} onChange={(e) => updateExperience(index, "end_date", e.target.value)} placeholder="Present" maxLength="20"/>
                          {expErrors.end_date && <p className="error-message">{expErrors.end_date}</p>}
                        </div>
                      </div>
                      <div className={`form-group ${expErrors.responsibilities ? 'error' : ''}`}>
                        <label>Responsibilities</label>
                        {(exp.responsibilities || [""]).map((resp, respIndex) => (
                          <div key={respIndex} className="point-input">
                            <textarea value={resp} onChange={(e) => updateResponsibility(index, respIndex, e.target.value)} placeholder="Describe your responsibility..." rows={3} maxLength="300"/>
                            <button onClick={() => removeResponsibility(index, respIndex)} className="remove-point-button">
                              🗑️
                            </button>
                          </div>
                        ))}
                        {expErrors.responsibilities && <p className="error-message">{expErrors.responsibilities}</p>}
                        <button onClick={() => addResponsibility(index)} className="add-point-button">
                          + Add Point
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeSection === 'skills' && (
            <div className="tab-panel">
              <h3 className="section-title">Skills</h3>
              
              <CategorizedSkillsInput
                skills={resumeData.skills || {}}
                onClear={() => {
                  const clearedSkills = {
                    programming_languages: [],
                    frameworks: [],
                    tools: [],
                    ai_ml_technologies: [],
                  };
                  onUpdate((prev) => ({
                    ...prev,
                    skills: clearedSkills,
                    ...(prev.professional_skills ? { professional_skills: [] } : {}),
                    ...(prev.soft_skills ? { soft_skills: [] } : {}),
                  }));
                }}
                clearLabel="Clear"
                onChange={(updatedSkills) => {
                  updateField('skills', updatedSkills);
                }}
                categories={[
                  { key: 'programming_languages', label: 'Programming Languages', placeholder: 'Add a programming language...' },
                  { key: 'frameworks', label: 'Frameworks', placeholder: 'Add a framework...' },
                  { key: 'tools', label: 'Tools', placeholder: 'Add a tool...' },
                  { key: 'ai_ml_technologies', label: 'AI/ML Technologies', placeholder: 'Add an AI/ML technology...' },
                ]}
              />

              {/* Professional Skills (for template defaults only) */}
              {resumeData.isTemplate && (
                <div className="form-group">
                  <label>Professional Skills</label>
                  <div className="add-skill-container">
                    <input
                      type="text"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder="Add a professional skill"
                      maxLength="50"
                    />
                    <button onClick={() => addSkill('professional_skills')}>Add</button>
                  </div>
                  <div className="skills-list flex flex-wrap gap-2">
                    {(resumeData.professional_skills || []).map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                        <button onClick={() => removeSkill('professional_skills', index)} className="remove-skill-btn">🗑️</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Soft Skills (for template defaults only) */}
              {resumeData.isTemplate && (
                <div className="form-group">
                  <label>Soft Skills</label>
                  <div className="add-skill-container">
                    <input
                      type="text"
                      value={newSoftSkill}
                      onChange={(e) => setNewSoftSkill(e.target.value)}
                      placeholder="Add a soft skill"
                      maxLength="50"
                    />
                    <button onClick={() => addSkill('soft_skills')}>Add</button>
                  </div>
                  <div className="skills-list flex flex-wrap gap-2">
                    {(resumeData.soft_skills || []).map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                        <button onClick={() => removeSkill('soft_skills', index)} className="remove-skill-btn">🗑️</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'education' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Education</h3>
                <button onClick={addEducation} className="add-button">
                  + Add Education
                </button>
              </div>
              {(resumeData.education || []).map((edu, index) => {
                 const eduErrors = validationErrors.education?.[index] || {};
                 return (
                  <div key={index} className="education-card">
                    <div className="card-header">
                      <h4>Education {index + 1}</h4>
                      <button onClick={() => removeEducation(index)} className="remove-button">
                        🗑️
                      </button>
                    </div>
                     <div className="card-content">
                      <div className={`form-group ${eduErrors.degree ? 'error' : ''}`}>
                        <label>Degree</label>
                        <input type="text" value={edu.degree || ""} onChange={(e) => updateEducation(index, "degree", e.target.value)} placeholder="Bachelor in Cybersecurity" maxLength="100"/>
                        {eduErrors.degree && <p className="error-message">{eduErrors.degree}</p>}
                      </div>
                       <div className={`form-group ${eduErrors.institution ? 'error' : ''}`}>
                        <label>Institution</label>
                        <input type="text" value={edu.institution || ""} onChange={(e) => updateEducation(index, "institution", e.target.value)} placeholder="University of Security" maxLength="100"/>
                        {eduErrors.institution && <p className="error-message">{eduErrors.institution}</p>}
                      </div>
                      <div className="form-group-grid">
                          <div className={`form-group ${eduErrors.start_date ? 'error' : ''}`}>
                            <label>Start Date</label>
                            <input type="text" value={edu.start_date || ""} onChange={(e) => updateEducation(index, "start_date", e.target.value)} placeholder="Aug 2018" maxLength="20"/>
                            {eduErrors.start_date && <p className="error-message">{eduErrors.start_date}</p>}
                          </div>
                          <div className={`form-group ${eduErrors.end_date ? 'error' : ''}`}>
                            <label>End Date</label>
                            <input type="text" value={edu.end_date || ""} onChange={(e) => updateEducation(index, "end_date", e.target.value)} placeholder="May 2022" maxLength="30"/>
                            {eduErrors.end_date && <p className="error-message">{eduErrors.end_date}</p>}
                          </div>
                      </div>
                      <div className={`form-group ${eduErrors.description ? 'error' : ''}`}>
                        <label>Description</label>
                        <textarea
                          value={edu.description || ""}
                          onChange={(e) => updateEducation(index, "description", e.target.value)}
                          placeholder="Brief description of your education..."
                          rows={3}
                          maxLength="300"
                        />
                        {eduErrors.description && <p className="error-message">{eduErrors.description}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeSection === 'languages' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Languages</h3>
                <button onClick={addLanguage} className="add-button">
                  + Add Language
                </button>
              </div>
              {(resumeData.languages || []).map((lang, index) => (
                <div key={index} className={`form-group ${validationErrors.languages?.[index] ? 'error' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="text"
                    value={lang}
                    onChange={(e) => updateLanguage(index, e.target.value)}
                    placeholder="e.g., English, Spanish"
                    style={{ flex: 1 }}
                    maxLength="30"
                  />
                  <button onClick={() => removeLanguage(index)} className="remove-button">
                    🗑️
                  </button>
                  {validationErrors.languages?.[index] && <p className="error-message" style={{position: 'absolute', top: '100%', left: 0, width: '100%'}}>{validationErrors.languages[index]}</p>}
                </div>
              ))}
            </div>
          )}

          {activeSection === 'achievements' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Achievements</h3>
                <button onClick={addAchievement} className="add-button">
                  + Add Achievement
                </button>
              </div>
              {(resumeData.achievements || []).map((achievement, index) => (
                <div key={index} className="experience-card">
                  <div className="card-header">
                    <h4>Achievement {index + 1}</h4>
                    <button onClick={() => removeAchievement(index)} className="remove-button">
                      🗑️
                    </button>
                  </div>
                  <div className="card-content">
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={achievement}
                        onChange={(e) => updateAchievement(index, e.target.value)}
                        placeholder="Describe your achievement..."
                        rows={3}
                        maxLength="200"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeSection === 'certifications' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Certifications</h3>
                <button onClick={addCertification} className="add-button">
                  + Add Certification
                </button>
              </div>
              {(resumeData.certifications || []).map((cert, index) => (
                <div key={index} className="experience-card">
                  <div className="card-header">
                    <h4>Certification {index + 1}</h4>
                    <button onClick={() => removeCertification(index)} className="remove-button">
                      🗑️
                    </button>
                  </div>
                  <div className="card-content">
                    <div className="form-group-grid">
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          type="text"
                          value={cert.name || ""}
                          onChange={(e) => updateCertification(index, "name", e.target.value)}
                          placeholder="CISSP"
                          maxLength="100"
                        />
                      </div>
                      <div className="form-group">
                        <label>Provider</label>
                        <input
                          type="text"
                          value={cert.provider || ""}
                          onChange={(e) => updateCertification(index, "provider", e.target.value)}
                          placeholder="ISC2"
                          maxLength="100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeSection === 'websites' && (
            <div className="tab-panel">
              <h3 className="section-title">Websites & Social Links</h3>
              <div className="form-group">
                <label>Personal Website</label>
                <input
                  type="url"
                  value={resumeData.personal_website || ""}
                  onChange={(e) => handlePersonalChange("personal_website", e.target.value)}
                  placeholder="https://johndoe.com"
                  maxLength="200"
                />
              </div>
              <div className="form-group">
                <label>GitHub</label>
                <input
                  type="url"
                  value={resumeData.github || ""}
                  onChange={(e) => handlePersonalChange("github", e.target.value)}
                  placeholder="https://github.com/johndoe"
                  maxLength="200"
                />
              </div>
              <div className="form-group">
                <label>LinkedIn</label>
                <input
                  type="url"
                  value={resumeData.linkedin || ""}
                  onChange={(e) => handlePersonalChange("linkedin", e.target.value)}
                  placeholder="https://linkedin.com/in/johndoe"
                  maxLength="200"
                />
              </div>
              <div className="form-group">
                <label>Portfolio</label>
                <input
                  type="url"
                  value={resumeData.portfolio || ""}
                  onChange={(e) => handlePersonalChange("portfolio", e.target.value)}
                  placeholder="https://portfolio.johndoe.com"
                  maxLength="200"
                />
              </div>
            </div>
          )}

          {activeSection === 'projects' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Projects</h3>
                <button onClick={addProject} className="add-button">
                  + Add Project
                </button>
              </div>
              {(resumeData.projects || []).map((proj, index) => (
                <div key={index} className="experience-card">
                  <div className="card-header">
                    <h4>Project {index + 1}</h4>
                    <button onClick={() => removeProject(index)} className="remove-button">
                      🗑️
                    </button>
                  </div>
                  <div className="card-content">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={proj.title || ""}
                        onChange={(e) => updateProject(index, "title", e.target.value)}
                        placeholder="Project Name"
                        maxLength="100"
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={proj.description || ""}
                        onChange={(e) => updateProject(index, "description", e.target.value)}
                        placeholder="Describe your project..."
                        rows={3}
                        maxLength="300"
                      />
                    </div>
                    <div className="form-group">
                      <label>Link</label>
                      <input
                        type="text"
                        value={proj.link || ''}
                        onChange={(e) => updateProject(index, "link", e.target.value)}
                        placeholder="https://github.com/your/project"
                        maxLength="100"
                      />
                    </div>
                    <div className="form-group">
                      <label>Year</label>
                      <input
                        type="text"
                        value={proj.year || ""}
                        onChange={(e) => updateProject(index, "year", e.target.value)}
                        placeholder="2025"
                        maxLength="10"
                      />
                    </div>
                    <div className="form-group">
                      <label>Technologies (comma separated)</label>
                      <input
                        type="text"
                        value={Array.isArray(proj.technologies) ? proj.technologies.join(', ') : ''}
                        onChange={(e) => {
                          const technologies = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                          updateProject(index, "technologies", technologies);
                        }}
                        placeholder="Python, Django, PostgreSQL"
                        maxLength="100"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityResumeForm;
